import DOMPurify from "dompurify";

/**
 * Turns a stranger's HTML into something safe to put in this document.
 *
 * The previous approach was a sandboxed iframe, which is airtight but cost two
 * things that matter more than they sound:
 *
 *   - **Height.** Without allow-scripts or allow-same-origin nothing can
 *     measure the content, so the frame got a fixed height and scrolled
 *     internally. Every message sat in a letterbox.
 *   - **Inline images.** Mail embeds images as `cid:` parts. A frame cannot
 *     resolve those, so a signature logo, an inline screenshot, or a
 *     newsletter's entire layout rendered as broken-image icons.
 *
 * Sanitising instead gives natural height and lets `cid:` be rewritten to real
 * object URLs. The safety argument is different but not weaker: DOMPurify
 * removes script, event handlers, and anything that can navigate or fetch on
 * its own, and the hooks below close the two gaps that matter for mail
 * specifically — links that could hijack this tab, and remote images that
 * report back to the sender.
 */

/**
 * Email is written with inline styles and tables; that is the medium. Stripping
 * `style` would make every well-designed message look broken, so it stays —
 * with `position` neutralised below so a message cannot escape its container.
 */
const ALLOWED_TAGS = [
  "a", "b", "blockquote", "br", "caption", "code", "col", "colgroup", "dd", "div",
  "dl", "dt", "em", "figcaption", "figure", "font", "h1", "h2", "h3", "h4", "h5",
  "h6", "hr", "i", "img", "li", "ol", "p", "pre", "q", "s", "small", "span",
  "strike", "strong", "sub", "sup", "table", "tbody", "td", "tfoot", "th",
  "thead", "tr", "u", "ul", "center", "big", "abbr", "address", "area", "map",
];

const ALLOWED_ATTR = [
  "align", "alt", "bgcolor", "border", "cellpadding", "cellspacing", "class",
  "color", "colspan", "dir", "face", "height", "href", "hspace", "lang", "name",
  "rowspan", "size", "src", "start", "style", "target", "title", "type",
  "valign", "vspace", "width", "cite", "datetime",
];

let configured = false;

function configure() {
  if (configured) return;
  configured = true;

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (!(node instanceof Element)) return;

    if (node.tagName === "A") {
      // Every link leaves this app. `noopener` matters: without it the opened
      // page gets a handle on this window and can navigate it somewhere that
      // looks like a sign-in screen.
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }

    if (node.tagName === "IMG") {
      const src = node.getAttribute("src") ?? "";
      // Remote images are deferred rather than removed, so "show images" can
      // put them back without re-sanitising. A tracking pixel that never loads
      // never tells the sender the message was opened.
      if (/^https?:/i.test(src)) {
        node.setAttribute("data-remote-src", src);
        node.removeAttribute("src");
      }
      node.setAttribute("loading", "lazy");
    }

    // A message must not be able to lift itself out of the reading pane, cover
    // the app chrome, or pin itself to the viewport.
    const style = node.getAttribute("style");
    if (style && /position\s*:\s*(fixed|absolute|sticky)/i.test(style)) {
      node.setAttribute("style", style.replace(/position\s*:\s*(fixed|absolute|sticky)\s*;?/gi, ""));
    }
  });
}

export interface SanitizeResult {
  html: string;
  /** True if anything was deferred and a "show images" prompt is warranted. */
  hasRemoteImages: boolean;
}

export function sanitizeEmailHtml(dirty: string): SanitizeResult {
  configure();
  const html = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Deliberately NOT setting ALLOWED_URI_REGEXP. DOMPurify tests that pattern
    // against every attribute value, not only the URI-bearing ones, so a
    // scheme-shaped regexp quietly strips `cellpadding="8"`, `bgcolor`,
    // `align`, `width` and `border` — the legacy presentational attributes that
    // table-based mail (which is to say, most mail Outlook produces) relies on
    // for its entire layout. The default pattern already permits http, https,
    // mailto, tel and cid while blocking javascript:, which is exactly what is
    // wanted here.
    ADD_DATA_URI_TAGS: ["img"],
    FORBID_TAGS: ["style", "link", "base", "form", "input", "button", "iframe", "object", "embed", "script"],
    FORBID_ATTR: ["srcset", "formaction", "background", "ping"],
    // A message is a document fragment, not a page.
    WHOLE_DOCUMENT: false,
    RETURN_DOM: false,
  });
  return { html, hasRemoteImages: /data-remote-src=/.test(html) };
}
